import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useAtomValue } from 'jotai/utils';
import { useAccessToken } from 'src/hooks/useAccessToken';
import { 
  getBulkDeleteCount, 
  requestBulkDelete,
  BulkDeleteCountResponse,
  BulkDeleteResponse,
  RetentionFilter
} from 'src/api/functions/assetsApi';
import { addonsSettingsAtom } from 'src/shared/settings/addons';
import { Button, PageBody, PageTitle, FeatureUnavailableBanner } from 'src/components';
import styles from './BulkDelete.module.scss';

// Icons
const CalendarIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
    <line x1="16" y1="2" x2="16" y2="6"/>
    <line x1="8" y1="2" x2="8" y2="6"/>
    <line x1="3" y1="10" x2="21" y2="10"/>
  </svg>
);

const FilterIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
  </svg>
);

const TrashIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/>
  </svg>
);

const PlusIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="12" y1="5" x2="12" y2="19"/>
    <line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);

const XIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="6" x2="6" y2="18"/>
    <line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

const CheckIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
  </svg>
);

const WarningIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
    <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
  </svg>
);

const InfoIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
  </svg>
);

const StorageIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4 7V4a2 2 0 012-2h8.5L20 7.5V20a2 2 0 01-2 2H6a2 2 0 01-2-2v-3"/>
    <polyline points="14 2 14 8 20 8"/>
    <path d="M10 15H4"/>
    <path d="M10 11H4"/>
    <path d="M10 19H4"/>
  </svg>
);

interface FilterItem {
  id: string;
  field: string;
  value: string;
}

type PageState = 'form' | 'loading' | 'success';

const BulkDelete: React.FC = () => {
  const history = useHistory();
  const accessToken = useAccessToken();

  // Bulk Purge is a per-account addon (BULK_PURGE_ASSETS), off by default and
  // enabled per-account by sysadmin. When it isn't granted the page is shown
  // read-only (content visible, the destructive action disabled).
  const addonsSettings = useAtomValue(addonsSettingsAtom);
  const bulkPurgeUnavailable = !(
    addonsSettings?.BULK_PURGE_ASSETS?.added &&
    addonsSettings?.BULK_PURGE_ASSETS?.enabled
  );
  
  // Form state
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [deleteFromStorage, setDeleteFromStorage] = useState(false);
  const [scope, setScope] = useState<'user' | 'group'>('user');
  const [filters, setFilters] = useState<FilterItem[]>([]);
  
  // Count state
  const [countData, setCountData] = useState<BulkDeleteCountResponse | null>(null);
  const [countLoading, setCountLoading] = useState(false);
  const [countError, setCountError] = useState<string | null>(null);
  
  // Request state
  const [pageState, setPageState] = useState<PageState>('form');
  const [requestError, setRequestError] = useState<string | null>(null);
  const [requestResult, setRequestResult] = useState<BulkDeleteResponse | null>(null);

  // Format date for API (ISO format)
  const formatDateForAPI = (dateString: string): string => {
    if (!dateString) return '';
    // Input is in format "YYYY-MM-DDTHH:mm" from datetime-local
    return dateString + ':00Z';
  };

  // Handle count preview
  const handleCountPreview = async () => {
    if (!startTime || !endTime) {
      setCountError('Please select both start and end dates');
      return;
    }

    setCountLoading(true);
    setCountError(null);
    setCountData(null);

    try {
      const response = await getBulkDeleteCount(
        {
          start_time: formatDateForAPI(startTime),
          end_time: formatDateForAPI(endTime),
          scope: scope,
        },
        accessToken
      );

      if ('status' in response && (response as any).status >= 400) {
        throw new Error((response as any).message || 'Failed to get count');
      }

      setCountData(response as BulkDeleteCountResponse);
    } catch (err: any) {
      setCountError(err.message || 'Failed to get asset count');
    } finally {
      setCountLoading(false);
    }
  };

  // Handle filter changes
  const addFilter = () => {
    setFilters([...filters, { id: Date.now().toString(), field: '', value: '' }]);
  };

  const removeFilter = (id: string) => {
    setFilters(filters.filter(f => f.id !== id));
  };

  const updateFilter = (id: string, field: 'field' | 'value', newValue: string) => {
    setFilters(filters.map(f => f.id === id ? { ...f, [field]: newValue } : f));
  };

  // Build retention filters for API
  const buildRetentionFilters = (): RetentionFilter[] => {
    return filters
      .filter(f => f.field && f.value)
      .map(f => ({
        term: { [`custom_data.${f.field}.keyword`]: f.value }
      }));
  };

  // Handle deletion request
  const handleRequestDeletion = async () => {
    if (!startTime || !endTime) {
      setRequestError('Please select both start and end dates');
      return;
    }

    setPageState('loading');
    setRequestError(null);

    try {
      const retentionFilters = buildRetentionFilters();
      
      const response = await requestBulkDelete(
        {
          start_time: formatDateForAPI(startTime),
          end_time: formatDateForAPI(endTime),
          retention_filters: retentionFilters.length > 0 ? retentionFilters : undefined,
          delete_from_storage: deleteFromStorage,
          scope: scope,
        },
        accessToken
      );

      if ('status' in response && (response as any).status >= 400) {
        throw new Error((response as any).message || 'Failed to request purge');
      }

      setRequestResult(response as BulkDeleteResponse);
      setPageState('success');
    } catch (err: any) {
      setRequestError(err.message || 'Failed to request bulk purge assets');
      setPageState('form');
    }
  };

  // Reset form
  const handleReset = () => {
    setStartTime('');
    setEndTime('');
    setDeleteFromStorage(false);
    setFilters([]);
    setCountData(null);
    setCountError(null);
    setRequestError(null);
    setRequestResult(null);
    setPageState('form');
  };

  // Loading state
  if (pageState === 'loading') {
    return (
      <div className={styles.pageWrapper}>
        <PageTitle title="Bulk Purge Assets" className={styles.pageTitle} />
        <PageBody narrow>
          <div className={styles.card}>
            <div className={styles.loadingContainer}>
              <div className={styles.spinner} />
              <p className={styles.loadingText}>Processing your bulk purge assets request...</p>
            </div>
          </div>
        </PageBody>
      </div>
    );
  }

  // Success state
  if (pageState === 'success' && requestResult) {
    return (
      <div className={styles.pageWrapper}>
        <PageTitle title="Bulk Purge Assets" className={styles.pageTitle} />
        <PageBody narrow>
          <div className={`${styles.card} ${styles.successCard}`}>
            <div className={styles.successIcon}>
              <CheckIcon />
            </div>
            <h2 className={styles.successTitle}>Confirmation Email Sent</h2>
            <p className={styles.successMessage}>
              {requestResult.message}
            </p>

            <div className={styles.successDetails}>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Status</span>
                <span className={styles.detailValue}>{requestResult.status}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Assets to Delete</span>
                <span className={styles.detailValue}>{requestResult.assets_to_delete}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Storage Deletion</span>
                <span className={styles.detailValue}>
                  {requestResult.storage_deletion?.enabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Expires At</span>
                <span className={styles.detailValue}>
                  {new Date(requestResult.expires_at).toLocaleString()}
                </span>
              </div>
            </div>

            <div className={styles.actions} style={{ justifyContent: 'center' }}>
              <Button mode="tertiary" onClick={() => history.push('/assets')}>
                Back to Assets
              </Button>
              <Button mode="primary" onClick={handleReset}>
                New Purge Request
              </Button>
            </div>
          </div>
        </PageBody>
      </div>
    );
  }

  // Form state
  return (
    <div className={styles.pageWrapper}>
      <PageTitle title="Bulk Purge Assets" className={styles.pageTitle} />
      <PageBody narrow>
        {bulkPurgeUnavailable && <FeatureUnavailableBanner />}

        <div className={styles.intro}>
          <p className={styles.pageSubtitle}>
            Purge multiple assets based on date range and metadata filters
          </p>
          <p className={styles.destructiveWarning}>
            ⚠️ This is a destructive operation that cannot be undone
          </p>
        </div>

        {requestError && (
          <div className={styles.errorMessage}>{requestError}</div>
        )}

        {/* Date Range Card */}
        <div className={styles.card}>
          <h2 className={styles.cardTitle}>
            <CalendarIcon />
            Date Range
          </h2>
          <p className={styles.cardSubtitle}>
            Select the time period for assets to be purged
          </p>

          <div className={styles.dateRangeGrid}>
            <div className={styles.dateField}>
              <label className={styles.label}>Start Date & Time</label>
              <input
                type="datetime-local"
                className={styles.dateInput}
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
              />
            </div>
            <div className={styles.dateField}>
              <label className={styles.label}>End Date & Time</label>
              <input
                type="datetime-local"
                className={styles.dateInput}
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
              />
            </div>
          </div>

          <div className={styles.countPreview}>
            <div className={styles.countDetails}>
              <span className={styles.countNumber}>
                {countLoading ? '...' : countData?.count ?? '—'}
              </span>
              <span className={styles.countLabel}>Assets will be purged</span>
              {countData && (
                <span className={styles.countMeta}>
                  {countData.within_limit 
                    ? `✓ Within limit (max: ${countData.max_allowed})`
                    : `⚠ Exceeds limit (max: ${countData.max_allowed})`
                  }
                </span>
              )}
            </div>
            <div className={styles.countButton}>
              <Button 
                mode="tertiary" 
                size="small"
                onClick={handleCountPreview}
                loading={countLoading}
                disabled={!startTime || !endTime}
              >
                Preview Count
              </Button>
            </div>
          </div>

          {countError && (
            <div className={styles.errorMessage} style={{ marginTop: '1rem' }}>
              {countError}
            </div>
          )}
        </div>

        {/* Retention Filters Card */}
        <div className={styles.card}>
          <h2 className={styles.cardTitle}>
            <FilterIcon />
            Retention Filters
            <span style={{ fontWeight: 'normal', fontSize: 'var(--font-size-m)', color: 'var(--color-gray-400)' }}>
              (Optional)
            </span>
          </h2>
          <p className={styles.cardSubtitle}>
            Specify metadata filters to retain certain assets from purging
          </p>

          {filters.length > 0 && (
            <div className={styles.filtersSection}>
              {filters.map((filter) => (
                <div key={filter.id} className={styles.filterRow}>
                  <div className={styles.dateField}>
                    <label className={styles.label}>Metadata Field</label>
                    <input
                      type="text"
                      className={styles.filterInput}
                      placeholder="e.g., assetCode"
                      value={filter.field}
                      onChange={(e) => updateFilter(filter.id, 'field', e.target.value)}
                    />
                  </div>
                  <div className={styles.dateField}>
                    <label className={styles.label}>Value</label>
                    <input
                      type="text"
                      className={styles.filterInput}
                      placeholder="e.g., MC"
                      value={filter.value}
                      onChange={(e) => updateFilter(filter.id, 'value', e.target.value)}
                    />
                  </div>
                  <button
                    className={styles.removeFilterBtn}
                    onClick={() => removeFilter(filter.id)}
                    title="Remove filter"
                  >
                    <XIcon />
                  </button>
                </div>
              ))}
            </div>
          )}

          <button className={styles.addFilterBtn} onClick={addFilter}>
            <PlusIcon />
            Add Retention Filter
          </button>

          <div className={styles.infoBox}>
            <InfoIcon />
            <span className={styles.infoText}>
              Retention filters match custom metadata fields in the assets. <strong>Assets matching these filters 
              will be excluded from purging. </strong>
            </span>
          </div>
        </div>

        {/* Deletion Scope Card */}
        <div className={styles.card}>
          <h2 className={styles.cardTitle}>
            <FilterIcon />
            Deletion Scope
          </h2>
          <p className={styles.cardSubtitle}>
            Choose whether to purge assets from only your account or from all assets in your user group 
          </p>

          <div className={styles.radioGroup}>
            <div className={styles.radioRow}>
              <input
                type="radio"
                id="scopeUser"
                name="scope"
                value="user"
                checked={scope === 'user'}
                onChange={(e) => setScope(e.target.value as 'user' | 'group')}
                className={styles.radio}
              />
              <label htmlFor="scopeUser" className={styles.radioLabel}>
                <span className={styles.radioTitle}>My Assets Only</span>
                <span className={styles.radioDescription}>
                  Purge only assets that belong to your  account
                </span>
              </label>
            </div>

            <div className={styles.radioRow}>
              <input
                type="radio"
                id="scopeGroup"
                name="scope"
                value="group"
                checked={scope === 'group'}
                onChange={(e) => setScope(e.target.value as 'user' | 'group')}
                className={styles.radio}
              />
              <label htmlFor="scopeGroup" className={styles.radioLabel}>
                <span className={styles.radioTitle}>Mine & User Group Assets</span>
                <span className={styles.radioDescription}>
                  Purge assets from your account and all users in your group
                </span>
              </label>
            </div>
          </div>
        </div>

        {/* Storage Options Card */}
        <div className={styles.card}>
          <h2 className={styles.cardTitle}>
            <StorageIcon />
            Storage Purge Options
          </h2>

          <div className={styles.checkboxRow}>
            <input
              type="checkbox"
              id="deleteFromStorage"
              className={styles.checkbox}
              checked={deleteFromStorage}
              onChange={(e) => setDeleteFromStorage(e.target.checked)}
            />
            <label htmlFor="deleteFromStorage" className={styles.checkboxLabel}>
              <span className={styles.checkboxTitle}>Purge from storage</span>
              <span className={styles.checkboxDescription}>
                Permanently delete files from cloud storage in addition to database records
              </span>
            </label>
          </div>

          <div className={styles.infoBox}>
            <InfoIcon />
            <span className={styles.infoText}>
              Enable this option to also delete the files from your storage. 
              When disabled (default), assets will be purged in FileSpin but files will remain in your storage.
              <br />
              <br />
              <strong>If you are using Filespin managed storage for Assets, assets will always be deleted from storage.</strong>
            </span>
          </div>
        </div>

        {/* Warning Box */}
        <div className={styles.warningBox}>
          <div className={styles.warningTitle}>
            <WarningIcon />
            Next Steps
          </div>
            <p className={styles.warningText}>
            <ul>
              <li>When you request bulk purge, you will receive an email with a mandatory confirmation link </li>
              <li>You must open the link and explicitly confirm to begin the purge </li>
              <li>Validate the Asset Purge details in the email are accurate before confirming</li>
              <li>Once purge starts, it cannot be stopped or cancelled</li>
              </ul>
          </p>
        </div>

        {/* Actions */}
        <div className={styles.actions}>
          <Button mode="tertiary" onClick={() => history.push('/assets')}>
            Cancel
          </Button>
          <Button 
            mode="decline" 
            onClick={handleRequestDeletion}
            disabled={bulkPurgeUnavailable || !startTime || !endTime}
          >            
            Request Purge
          </Button>
        </div>
      </PageBody>
    </div>
  );
};

export default BulkDelete;
